import { Loader2, Download, Sparkles, RefreshCw, Mail, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { BriefMarkdown } from './capabilityShared';

export function ImpactBriefDialog({
  open, onOpenChange, briefLoading, briefData, briefTitle, briefPayload,
  showEmail, emailTo, emailNote, emailSending,
  onRegenerate, onToggleEmail, onDownload, onEmailToChange, onEmailNoteChange, onSendEmail,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cap-brief-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-cyan-600" /> {briefTitle || 'AI Impact & Action Plan'}
          </DialogTitle>
          <DialogDescription className="sr-only">AI-generated impact and action plan with recommended interventions.</DialogDescription>
        </DialogHeader>
        {briefLoading ? (
          <div className="py-12 flex flex-col items-center justify-center text-slate-400 gap-2" data-testid="cap-brief-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
            <p className="text-xs">Generating executive brief…</p>
          </div>
        ) : !briefData ? (
          <div className="py-10 text-center text-sm text-slate-400">No brief available.</div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-4">
              <BriefMarkdown text={briefData.brief_markdown} />
            </div>
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-slate-400">
                GPT-4o · {briefData.cached ? 'cached' : 'freshly generated'}
                {briefData.generated_at ? ` · ${new Date(briefData.generated_at).toLocaleString()}` : ''}
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
                  onClick={() => onRegenerate(briefPayload, true)} disabled={briefLoading}
                  data-testid="cap-brief-regenerate">
                  <RefreshCw className="h-3.5 w-3.5" /> Regenerate
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
                  onClick={onToggleEmail}
                  data-testid="cap-brief-email-toggle">
                  <Mail className="h-3.5 w-3.5" /> Email to owner
                </Button>
                <Button size="sm" className="h-8 text-xs gap-1.5 bg-cyan-600 hover:bg-cyan-700"
                  onClick={onDownload} data-testid="cap-brief-download">
                  <Download className="h-3.5 w-3.5" /> Download as Markdown
                </Button>
              </div>
            </div>
            {showEmail && (
              <div className="rounded-lg border border-cyan-200 bg-cyan-50/40 p-3 space-y-2" data-testid="cap-brief-email-form">
                <p className="text-xs font-semibold text-slate-700">Email this action plan to the accountable owner</p>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => onEmailToChange(e.target.value)}
                  placeholder="manager@company.com"
                  className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  data-testid="cap-brief-email-input"
                />
                <textarea
                  value={emailNote}
                  onChange={(e) => onEmailNoteChange(e.target.value)}
                  placeholder="Optional note to the recipient…"
                  rows={2}
                  className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  data-testid="cap-brief-email-note"
                />
                <div className="flex justify-end">
                  <Button size="sm" className="h-8 text-xs gap-1.5 bg-cyan-600 hover:bg-cyan-700"
                    onClick={onSendEmail} disabled={emailSending}
                    data-testid="cap-brief-email-send">
                    {emailSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Send email
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
